import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated, getUserRole, canAccessRoute } from '../../utils/helpers';
import { authAPI } from '../../api/endpoints';
import { setUser, removeUser } from '../../utils/helpers';
import LoadingSpinner from './LoadingSpinner';

const ProtectedRoute = ({ children, requiredRole }) => {
  const location = useLocation();
  const [isVerifying, setIsVerifying] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [userRole, setUserRole] = useState(null);

  // Public routes that don't require authentication
  const publicRoutes = ['/login', '/register', '/forgot-password', '/reset-password'];
  const isPublicRoute = publicRoutes.some(route => location.pathname === route || location.pathname.startsWith(`${route}/`));

  useEffect(() => {
    // If it's a public route, don't verify authentication
    if (isPublicRoute) {
      setIsVerifying(false);
      return;
    }

    const verifyAuth = async () => {
      try {
        // Check if user data exists in localStorage
        if (!isAuthenticated()) {
          setIsVerifying(false);
          return;
        }

        // Verify with backend
        const response = await authAPI.verify();
        const { user: userData } = response.data;
        
        // Update user data
        setUser(userData);
        setUserRole(userData.role);
        setIsValid(true);
      } catch (error) {
        // Token is invalid or expired
        console.error('Auth verification failed:', error);
        removeUser();
        setIsValid(false);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyAuth();
  }, [isPublicRoute]);

  // If it's a public route, render children directly
  if (isPublicRoute) {
    return children;
  }

  if (isVerifying) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <LoadingSpinner size="large" text="Verifying authentication..." />
      </div>
    );
  }

  if (!isValid) {
    // Avoid redirect loop if somehow rendered on login path
    if (isPublicRoute || location.pathname.startsWith('/login')) {
      return children;
    }
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && userRole !== requiredRole) {
    // Redirect to appropriate dashboard if user doesn't have required role
    const roleRoutes = {
      student: '/learner',
      parent: '/parent',
      teacher: '/teacher',
      publisher: '/publisher',
      admin: '/admin',
      owner: '/owner',
    };
    
    return <Navigate to={roleRoutes[userRole] || '/login'} replace />;
  }

  return children;
};

export default ProtectedRoute;

